import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Star, Save, Loader2, ExternalLink } from 'lucide-react';
import { API } from '@/config';

/**
 * SiteSettingsPanel — Admin UI to set Google Review URL and social links.
 * Used by the GoogleReviewPrompt and footer/share buttons.
 */
const SiteSettingsPanel = ({ token, i18n }) => {
  const ar = i18n.language === 'ar';
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    google_review_url: '',
    facebook_url: '',
    instagram_url: '',
    tiktok_url: '',
    youtube_url: '',
    twitter_url: '',
    linkedin_url: '',
    telegram_url: '',
    whatsapp_url: '',
  });

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/site/settings/public`)
      .then((r) => setForm((prev) => ({ ...prev, ...r.data })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site/settings`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(ar ? 'تم الحفظ' : 'Saved successfully');
    } catch (e) {
      toast.error(ar ? 'فشل الحفظ' : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="site-settings-panel">
      <div className="bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Star className="w-8 h-8" fill="currentColor" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">
              {ar ? 'إعدادات الموقع — مراجعات Google' : 'Site Settings — Google Reviews'}
            </h2>
            <p className="text-white/90 text-sm mt-1 max-w-2xl">
              {ar
                ? 'أضف رابط Google Review الخاص بك. سيظهر الزر تلقائياً للمستخدمين بعد إكمال 3 اختبارات بنجاح.'
                : 'Set your Google Review URL. It is automatically prompted to users after they complete 3 tests successfully.'}
            </p>
          </div>
        </div>
      </div>

      {/* Google Review URL */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Star className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {ar ? 'رابط Google Review' : 'Google Review URL'}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {ar
                ? 'الرابط الذي يفتح صفحة كتابة المراجعة على Google Maps لنشاطك التجاري'
                : 'The URL that opens your Google Maps review page'}
            </p>
          </div>
        </div>

        <input
          type="url"
          value={form.google_review_url}
          onChange={set('google_review_url')}
          placeholder="https://g.page/r/CXXXXXXX/review"
          disabled={loading}
          data-testid="input-google-review-url"
          className="w-full mt-3 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
          dir="ltr"
        />

        <div className="mt-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
          <strong className="block mb-1">{ar ? 'كيف تحصل على هذا الرابط؟' : 'How to get this URL?'}</strong>
          <ol className="list-decimal ms-4 space-y-1">
            <li>{ar ? 'اذهب إلى Google Business Profile' : 'Go to your Google Business Profile'}</li>
            <li>{ar ? 'اضغط "اطلب مراجعات"' : 'Click "Ask for reviews"'}</li>
            <li>{ar ? 'انسخ الرابط الذي يبدأ بـ https://g.page/r/' : 'Copy the link that starts with https://g.page/r/'}</li>
          </ol>
        </div>

        {form.google_review_url && (
          <a
            href={form.google_review_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            <ExternalLink className="w-4 h-4" />
            {ar ? 'اختبر الرابط' : 'Test the link'}
          </a>
        )}
      </div>

      {/* Social URLs (optional) — leave a field empty to hide that icon
          from the public footer. */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
          {ar ? 'روابط التواصل الاجتماعي' : 'Social Links'}
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
          {ar
            ? 'الحقول الفارغة لا تظهر في الموقع. الزر يظهر تلقائياً عند إضافة رابط.'
            : 'Empty fields are hidden from the site. The icon appears automatically when a URL is added.'}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input type="url" value={form.facebook_url} onChange={set('facebook_url')} placeholder="https://facebook.com/yourpage" className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:border-slate-700" dir="ltr" data-testid="social-facebook-input" />
          <input type="url" value={form.instagram_url} onChange={set('instagram_url')} placeholder="https://instagram.com/yourpage" className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:border-slate-700" dir="ltr" data-testid="social-instagram-input" />
          <input type="url" value={form.tiktok_url} onChange={set('tiktok_url')} placeholder="https://tiktok.com/@yourpage" className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:border-slate-700" dir="ltr" data-testid="social-tiktok-input" />
          <input type="url" value={form.youtube_url} onChange={set('youtube_url')} placeholder="https://youtube.com/@yourchannel" className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:border-slate-700" dir="ltr" data-testid="social-youtube-input" />
          <input type="url" value={form.twitter_url} onChange={set('twitter_url')} placeholder="https://x.com/yourpage" className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:border-slate-700" dir="ltr" data-testid="social-twitter-input" />
          <input type="url" value={form.linkedin_url} onChange={set('linkedin_url')} placeholder="https://linkedin.com/company/yourpage" className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:border-slate-700" dir="ltr" data-testid="social-linkedin-input" />
          <input type="url" value={form.telegram_url} onChange={set('telegram_url')} placeholder="https://t.me/yourchannel" className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:border-slate-700" dir="ltr" data-testid="social-telegram-input" />
          <input type="url" value={form.whatsapp_url} onChange={set('whatsapp_url')} placeholder="https://wa.me/46701234567" className="px-3 py-2 rounded-lg border dark:bg-slate-900 dark:border-slate-700" dir="ltr" data-testid="social-whatsapp-input" />
        </div>
      </div>

      <Button
        onClick={save}
        disabled={saving || loading}
        className="w-full md:w-auto bg-emerald-600 hover:bg-emerald-700 text-white"
        data-testid="save-site-settings-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Save className="w-4 h-4 me-2" />}
        {ar ? 'حفظ' : 'Save'}
      </Button>
    </div>
  );
};

export default SiteSettingsPanel;
